import React, { useState, useEffect, useRef } from 'react';
import {
  FileSignature,
  Home,
  FileStack,
  ChevronDown,
  ChevronLeft,
  Handshake,
  FolderDown,
  Boxes
} from "lucide-react";
import './style.css';
import logoNexus from '../../assets/images/logo_nexus.png';
import logoNexusFull from '../../assets/images/nexus_logo.png';
import { Link, useLocation } from 'react-router-dom';
import { PermissionGate } from '../../contexts/PermissionGate';
import { useAuthContext } from '../../contexts/AuthContext';
import { TIPOS_POR_GRUPO } from '../../screens/cadastros/ativos/camposConfig';

interface SubItem {
  label: string;
  href?: string;
  requiredPermission?: string;
  anyPermission?: string[];
  children?: SubItem[];
}

interface MenuItem {
  label: string;
  icon: React.ReactNode;
  href?: string;
  requiredPermission?: string;
  submenu?: {
    label: string;
    items: SubItem[];
  };
}

const Sidebar: React.FC = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(!isHomePage);
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { permissions } = useAuthContext();

  const podeVer = (req?: string, anyOf?: string[]) => {
    if (req) return permissions.includes(req);
    if (anyOf) return anyOf.some((p) => permissions.includes(p));
    return true;
  };
  // visível se folha permitida ou se tem algum descendente visível
  const subVisivel = (s: SubItem): boolean =>
    s.children ? s.children.some(subVisivel) : podeVer(s.requiredPermission, s.anyPermission);

  const [expandedSub, setExpandedSub] = useState<string[]>([]);
  const toggleSub = (label: string) =>
    setExpandedSub((prev) => (prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]));

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(!isHomePage);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, [isHomePage]);

  useEffect(() => {
    if (!isMobile) {
      setIsCollapsed(!isHomePage);
    }
  }, [location.pathname, isMobile, isHomePage]);

  const toggleSubmenu = (label: string) => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
    setExpandedItems(prev =>
      prev.includes(label)
        ? []
        : [label]
    );
  };

  const handleSubItemClick = () => {
    setExpandedItems([]);
    if (isMobile) {
      setIsCollapsed(true);
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: 'Home',
      icon: <Home size={22} />,
      href: '/',
    },
    {
      label: 'Aquisição',
      icon: <FileStack size={22} />,
      submenu: {
        label: 'Aquisição',
        items: [
          { label: 'Overview', href: '/aquisicao/operacoes/overview', requiredPermission: 'VIEW_OVERVIEW' },
          { label: 'Operações', href: '/aquisicao/operacoes', requiredPermission: 'VIEW_OPERATIONS' },
          { label: 'Registros', href: '/aquisicao/registros', requiredPermission: 'VIEW_RECORDS' },
          { label: 'Lastros', href: '/aquisicao/lastros', requiredPermission: 'VIEW_BACKINGS' }
        ],
      },
    },
    {
      label: 'Cadastros',
      icon: <FileSignature size={22} />,
      submenu: {
        label: 'Cadastros',
        items: [
          {
            label: 'Fundos',
            children: [
              { label: 'Líquidos', href: '/cadastro/fundo/liquidos', requiredPermission: 'VIEW_FUNDS_LIQUIDOS' },
              { label: 'Estruturados', href: '/cadastro/fundo/estruturados', requiredPermission: 'VIEW_FUNDS' },
            ],
          },
          { label: 'Administradores', href: '/cadastros/cadastro-admin', requiredPermission: 'VIEW_ADMINS' },
          { label: 'Cedentes', href: '/cadastros/cadastro-cedente', requiredPermission: 'VIEW_ASSIGNORS' },
          { label: 'Originadores', href: '/cadastros/cadastro-originador', requiredPermission: 'VIEW_ORIGINATORS' },
          { label: 'Prestadores', href: '/cadastro/prestador' },
          { label: 'Usuários', href: '/cadastros/cadastro-usuario', requiredPermission: 'VIEW_USERS' },
        ],
      },
    },
    {
      label: 'Ativos',
      icon: <Boxes size={22} />,
      submenu: {
        label: 'Ativos',
        items: [
          { label: 'Portfolio', href: '/ativos/portfolio', requiredPermission: 'VIEW_FUNDS' },
          {
            label: 'Cadastros',
            children: TIPOS_POR_GRUPO.map((g) => ({
              label: g.grupoLabel,
              children: g.tipos.map((t) => ({
                label: t.label,
                href: `/cadastro/ativo/${t.tipo}`,
                requiredPermission: 'VIEW_FUNDS',
              })),
            })),
          },
        ],
      },
    },
    {
      label: 'Conciliações',
      icon: <Handshake size={22} />,
      submenu: {
        label: 'Conciliações',
        items: [
          { label: 'Gerais', href: '/conciliacoes/gerais', requiredPermission: 'VIEW_OPERATIONS' },
        ],
      },
    },
    {
      label: 'Documentos',
      icon: <FolderDown size={22} />,
      href: '/documentos',
    }
  ];
  const matchesPath = (href: string): boolean =>
    location.pathname === href || location.pathname.startsWith(`${href}/`);

  const subItemActive = (s: SubItem): boolean =>
    s.href ? matchesPath(s.href) : !!s.children?.some(subItemActive);

  const isItemActive = (item: MenuItem): boolean => {
    if (item.href) {
      return item.href === '/'
        ? location.pathname === '/'
        : matchesPath(item.href);
    }
    if (item.submenu) {
      return item.submenu.items.some(subItemActive);
    }
    return false;
  };

  // renderiza um sub-item: folha vira link; nó com filhos vira grupo expansível (recursivo)
  const renderSubItem = (sub: SubItem, key: React.Key, nested = false): React.ReactNode => {
    if (!subVisivel(sub)) return null;
    const liClass = `submenu-item${nested ? ' submenu-item--nested' : ''}`;
    if (!sub.children) {
      return (
        <li key={key} className={liClass}>
          <Link to={sub.href!} className={matchesPath(sub.href!) ? 'active' : ''} onClick={handleSubItemClick}>
            {sub.label}
          </Link>
        </li>
      );
    }
    const aberto = expandedSub.includes(sub.label);
    return (
      <li key={key} className={liClass}>
        <button type="button" className="submenu-parent" onClick={() => toggleSub(sub.label)}>
          <span>{sub.label}</span>
          <ChevronDown size={14} className={`submenu-chevron ${aberto ? 'expanded' : ''}`} />
        </button>
        {aberto && (
          <ul className="submenu-ul">
            {sub.children.map((c, i) => renderSubItem(c, i, true))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {isMobile && !isCollapsed && (
        <div
          className="sidebar-overlay active"
          onClick={() => setIsCollapsed(true)}
        />
      )}
      <div
        ref={sidebarRef}
        className={`sidebar ${isCollapsed ? 'collapsed' : 'expanded'}`}
      >
        <button
          className="sidebar-collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
          aria-label={isCollapsed ? 'Expandir menu' : 'Recolher menu'}
        >
          <ChevronLeft size={18} className={isCollapsed ? 'rotated' : ''} />
        </button>

        <div className="sidebar-logo">
          <img
            src={isCollapsed ? logoNexus : logoNexusFull}
            alt="Logo"
            className={isCollapsed ? "sidebar-logo-closed" : "sidebar-logo-open"}
          />
        </div>

        <hr className="sidebar-divider" />

        <nav className="sidebar-menu">
          {menuItems.map((item, index) => {
            const isExpanded = expandedItems.includes(item.label);
            const active = isItemActive(item);

            const menuContent = (
              <div
                key={index}
                className={`sidebar-item-container ${isExpanded ? 'open' : ''}`}
              >
                {item.href && !item.submenu ? (
                  <Link
                    to={item.href}
                    className={`sidebar-item ${active ? 'active' : ''}`}
                    title={isCollapsed ? item.label : undefined}
                    onClick={handleSubItemClick}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    <span className="sidebar-label">{item.label}</span>
                  </Link>
                ) : (
                  <button
                    className={`sidebar-item ${active ? 'active' : ''} ${isExpanded ? 'open' : ''}`}
                    onClick={() => item.submenu && toggleSubmenu(item.label)}
                    title={isCollapsed ? item.label : undefined}
                  >
                    <span className="sidebar-icon">{item.icon}</span>
                    <span className="sidebar-label">{item.label}</span>
                    {item.submenu && (
                      <ChevronDown
                        size={16}
                        className={`submenu-chevron ${isExpanded ? 'expanded' : ''}`}
                      />
                    )}
                  </button>
                )}

                {item.submenu && (
                  <div
                    className={`sub-menu-container ${isCollapsed ? 'flyout' : ''} ${isExpanded && !isCollapsed ? 'expanded' : ''}`}
                  >
                    {isCollapsed && (
                      <span className="flyout-title">{item.submenu.label}</span>
                    )}
                    <ul className="submenu-ul">
                      {item.submenu.items.map((subItem, subIndex) => renderSubItem(subItem, subIndex))}
                    </ul>
                  </div>
                )}
              </div>
            );

            return item.requiredPermission ? (
              <PermissionGate key={index} required={item.requiredPermission}>
                {menuContent}
              </PermissionGate>
            ) : menuContent;
          })}
        </nav>
      </div>
    </>
  );
};

export default Sidebar;
